import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldAlert, Search, LayoutDashboard, ChevronRight, Shield, Clock, Lock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { setupApi } from '@/lib/api';

const HomePage = () => {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);

  useEffect(() => {
    // Auto-setup on first load
    const doSetup = async () => {
      try {
        await setupApi.initialize();
        setIsSetup(true);
      } catch (e) {
        // Already setup
        setIsSetup(true);
      }
    };
    doSetup();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-slate-900" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                COGNIEtica
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/denunciar/demo" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                Submeter Denúncia
              </Link>
              <Link to="/seguimento" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                Acompanhar
              </Link>
              <Button variant="outline" size="sm" onClick={() => navigate('/login')} data-testid="login-btn">
                Área Reservada
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Canal de Denúncias
              <span className="block text-sky-400 mt-2">Seguro e Confidencial</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Plataforma em conformidade com a Lei n.º 93/2021 para proteção de denunciantes. 
              Submeta denúncias de forma anónima ou identificada com total segurança.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-white text-slate-900 hover:bg-slate-100"
                onClick={() => navigate('/denunciar/demo')}
                data-testid="submit-report-hero-btn"
              >
                <ShieldAlert className="w-5 h-5 mr-2" />
                Submeter Denúncia
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-600 text-white hover:bg-slate-800"
                onClick={() => navigate('/seguimento')}
                data-testid="track-report-hero-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                Acompanhar Denúncia
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Proteção Total do Denunciante
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Garantimos a confidencialidade e segurança de todas as denúncias, 
              em conformidade com a legislação europeia e portuguesa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Anonimato Garantido</h3>
                <p className="text-slate-600 text-sm">
                  Submeta denúncias de forma completamente anónima. 
                  O seu IP não é registado e todos os metadados são removidos.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Proteção Legal</h3>
                <p className="text-slate-600 text-sm">
                  Lei 93/2021 garante proteção contra represálias. 
                  O denunciante de boa-fé está legalmente protegido.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Prazos Legais</h3>
                <p className="text-slate-600 text-sm">
                  Confirmação de receção em 7 dias. Feedback sobre seguimento em 3 meses. 
                  Monitorização automática de prazos.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Tem conhecimento de irregularidades?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Denunciar é um ato de coragem. A sua denúncia pode fazer a diferença.
              Garantimos total confidencialidade.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-slate-100"
              onClick={() => navigate('/denunciar/demo')}
            >
              Submeter Denúncia Agora
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-white" />
                <span className="text-white font-semibold">COGNIEtica</span>
              </div>
              <p className="text-sm">
                Plataforma de canal de denúncias em conformidade com a Lei 93/2021.
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Legislação</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://dre.pt/application/conteudo/176065058" target="_blank" rel="noopener noreferrer" className="hover:text-white">Lei 93/2021</a></li>
                <li><a href="#" className="hover:text-white">Diretiva UE 2019/1937</a></li>
                <li><a href="#" className="hover:text-white">RGPD</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Recursos</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">FAQ</a></li>
                <li><a href="#" className="hover:text-white">Política de Privacidade</a></li>
                <li><a href="#" className="hover:text-white">Termos de Uso</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4">Contactos Externos</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://www.menac.gov.pt" target="_blank" rel="noopener noreferrer" className="hover:text-white">MENAC</a></li>
                <li><a href="#" className="hover:text-white">Ministério Público</a></li>
                <li><a href="#" className="hover:text-white">Provedoria</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} CogniEtica - Canal de denúncias by{' '}
              <a href="https://cogniseg.com" target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Cogniseg</a>
              <br />
              Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
